import React, { useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';

import { getSiteCloudId, MAX_SELECTED_SITES } from '../../../../../common/constants/filters/site';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { onSiteSelectorShown } from '../../../../../common/utils/events/site-selector-shown';
import { getDefaultSiteIds } from '../../../../../common/utils/sites/default-sites';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useCloudId,
	usePrimaryProduct,
	useUnitMetadata,
} from '../../../../../controllers/store/bootstrap';
import { siteFilterId } from '../../../../../controllers/store/search/filters/filter-ids';
import { i18n } from './messages';
import {
	buildCurrentSiteOption,
	SELECTED_GROUP,
	SelectedHeading,
	SiteOptionLabel,
	sortAlphabetically,
	UNSELECTED_GROUP,
} from './utils';

const SiteSelectFilter = ({
	isFilterInDropdown,
	onFilterChange,
	hideOnHoverOut,
	defaultIsOpen,
	collaborationContext,
}: FilterProps & {
	defaultIsOpen?: boolean;
	collaborationContext?: { orgName?: string; unitName?: string };
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const hasFiredShownEvent = useRef(false);
	const [primarySiteId] = useCloudId();
	const [primaryProduct] = usePrimaryProduct();
	const unitMetadata = useUnitMetadata();
	const defaults = useMemo(
		() => getDefaultSiteIds(unitMetadata, primarySiteId),
		[unitMetadata, primarySiteId],
	);
	const { orgName, unitName } = collaborationContext ?? {};
	const primarySiteName =
		unitMetadata?.find((site) => getSiteCloudId(site.id) === primarySiteId)?.siteName ??
		primarySiteId;
	const [filterData, { loadInitialOptions, setOption: setOptionForSelectFilter }] = useSelectFilter(
		{
			filterId: siteFilterId,
		},
	);

	useEffect(() => {
		if (filterData && !hasFiredShownEvent.current) {
			hasFiredShownEvent.current = true;
			fireAnalyticsEvent(onSiteSelectorShown({ commonAttributes }));
		}
	}, [commonAttributes, filterData, fireAnalyticsEvent]);

	// Custom rendering of the first row displaying the current site to append "(current)" as italicised
	const formatOptionLabel = useMemo(
		() =>
			(option: {
				label?: React.ReactNode;
				value?: string | number;
				isSelected?: boolean;
			}): React.ReactNode => (
				<SiteOptionLabel option={option} isCurrent={option.value === primarySiteId} />
			),
		[primarySiteId],
	);

	// Only show the "(Select up to N)" hint when the tenant has enough sites to reach the cap.
	const capReachable = (unitMetadata?.length ?? 0) >= MAX_SELECTED_SITES;

	// Multi-site only makes sense with more than one site to search across. Every existing caller
	// (main.tsx, product-filters/index.tsx) already gates on this exact `?? 0` form before mounting
	// the header's selector, treating "not yet loaded" the same as "not multi-site" — hidden, not
	// an optimistic fallback. The Filters dropdown has no such upstream gate, so it needs the same
	// check here instead.
	if (!filterData || (unitMetadata?.length ?? 0) <= 1) {
		return null;
	}

	const { selectedOptions, options, isLoading } = filterData;

	const currentSiteOption = buildCurrentSiteOption(primarySiteId, primarySiteName);

	const selectedSites = selectedOptions
		.filter((option) => option.value !== primarySiteId)
		.sort(sortAlphabetically);

	const unselectedSites = options
		.filter((option) => option.value !== primarySiteId && !option.isSelected)
		.sort(sortAlphabetically);

	const selectedGroupOptions = currentSiteOption
		? [
				currentSiteOption, // Current site is always displayed first
				...selectedSites,
			]
		: selectedSites;

	const isDefaultSitesCohort =
		serveFpsMultiSiteSearchExperiment({ primaryProduct, fireExperimentExposure: false }) ===
		'test-2';
	const isDefaultSelection =
		isDefaultSitesCohort &&
		selectedGroupOptions.length === defaults.length &&
		defaults.every((id) => selectedGroupOptions.some((option) => option.value === id));
	const isSiteFilterActive = isDefaultSitesCohort
		? !isDefaultSelection
		: selectedGroupOptions.length > 1;
	const isAllSitesDefaultSelection =
		isDefaultSelection && (unitMetadata?.length ?? 0) <= MAX_SELECTED_SITES;
	const label = isAllSitesDefaultSelection
		? formatMessage(i18n.allSites)
		: primarySiteName || formatMessage(i18n.label);

	const atCap = selectedGroupOptions.length >= MAX_SELECTED_SITES;
	const unselectedGroupOptions = atCap
		? unselectedSites.map((option) => ({
				...option,
				isDisabled: true, // Disable the rest of the options if over the cap
			}))
		: unselectedSites;

	const groupedOptions = [
		{ label: SELECTED_GROUP, options: selectedGroupOptions },
		{ label: UNSELECTED_GROUP, options: unselectedGroupOptions },
	].filter((group) => group.options.length > 0);

	return (
		<SelectFilter
			// "compact" caps the button at 180px, sized for the standalone header row alongside the
			// app selector and other product filters. Inside the Filters dropdown every row instead
			// fills the row's full width, like every other filter there.
			contentFit={isFilterInDropdown ? 'default' : 'compact'}
			checkboxIndicators
			isSearchable
			filterOption={null}
			placeholder={formatMessage(i18n.findSitesPlaceholder)}
			defaultIsOpen={defaultIsOpen}
			onDropdownOpen={loadInitialOptions}
			onSelectChange={(option) => {
				const changed = Array.isArray(option) ? option[0] : option;
				// Never route the current/primary site into the filter — it is display-only and must stay selected
				if (!changed || changed.value === primarySiteId) {
					return;
				}
				// Cap backstop: disabled options already block the click; guard selecting a new site at the cap.
				if (atCap && !changed.isSelected) {
					return;
				}
				setOptionForSelectFilter(changed);
				onFilterChange?.(changed);
			}}
			options={groupedOptions}
			selectedOptions={selectedGroupOptions}
			label={label}
			showSelectedCount={!isAllSitesDefaultSelection}
			filterOrControlId={'site'}
			isLoading={isLoading}
			formatOptionLabel={formatOptionLabel}
			isFilterInDropdown={isFilterInDropdown}
			hideOnHoverOut={hideOnHoverOut}
			iconBefore={<OfficeBuildingIcon label="" />}
			// The unselected group is titled by the collaboration unit/org name when available, otherwise
			// "Site"/"Sites" (pluralised on the count).
			formatGroupLabel={(group) =>
				group.label === SELECTED_GROUP ? (
					<SelectedHeading atCap={atCap} capReachable={capReachable} />
				) : (
					(unitName ??
					orgName ??
					formatMessage(i18n.unselectedHeading, { count: group.options.length }))
				)
			}
			// Any departure from the test-2 default scope is active, including primary-only.
			isSelectedOverride={isSiteFilterActive}
			// The neutral (grey-filled) chip treatment is specific to the standalone header button.
			// Inside the Filters dropdown every row uses the same default unselected/selected styling.
			hasNeutralBackground={isFilterInDropdown ? undefined : !isSiteFilterActive}
			// The label already describes the site scope, so there is nothing further to append.
			getFirstSelectedLabel={() => ''}
		/>
	);
};

export default SiteSelectFilter;
